import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Modal,
    Stack,
    Typography,
} from '@mui/material';
import { Loading, Notify, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Roulette from '../../../components/game/Roulette';
import { getActiveGifts, registerGift } from '../../../data/api/gifts/GiftsRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

/**
 * Modal de regalos activos para reclamar.
 *
 * Lenguaje visual alineado con `GiftsMake` y los demás modales del módulo
 * (header con icono dorado, secciones internas con borde tenue, items
 * tipo "tarjeta" en lugar de filas planas).
 *
 * Comportamiento:
 *  - Regalo de jugadas fijas → click reclama directo y dispara confirmación.
 *  - Regalo tipo ruleta → click abre el sub-modal con la ruleta y, al
 *    girarla, dispara la reclamación.
 */
const Gifts = ({ handleClose }) => {
    const { token, validateUser } = useIndex();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [giftsList, setGiftsList] = useState([]);
    const [rouletteValue, setRouletteValue] = useState('');
    const [roulettePrize, setRoulettePrize] = useState('');
    const [wasActive, setWasActive] = useState(false);
    const [giftSelect, setGiftSelect] = useState(0);
    const [giftType, setGiftType] = useState(0);
    const [claimingId, setClaimingId] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (giftSelect && roulettePrize && rouletteValue && !giftType) {
            registerUserGift();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [giftSelect, roulettePrize, rouletteValue]);

    const handleOpeneMod = (item) => {
        setGiftSelect(item?.id);
        setGiftType(item?.gift_type);
        setClaimingId(item?.id);

        if (item?.gift_type) {
            setOpen(true);
        } else {
            setRoulettePrize(`${item?.moves} Jugadas`);
            setRouletteValue(item?.moves);
        }
    };

    const handleCloseMod = () => {
        resetData();
        setOpen(false);
    };

    const resetData = () => {
        setGiftSelect(0);
        setRoulettePrize('');
        setRouletteValue(0);
        setClaimingId(null);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const { data } = await getActiveGifts(token);
            if (data?.success) setGiftsList(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const registerUserGift = async () => {
        Loading.standard();
        try {
            const dataSend = {
                gifts_id: giftSelect,
                prize_win: roulettePrize,
                prize_value: rouletteValue,
            };
            const { data } = await registerGift(token, dataSend);
            if (data?.success) {
                Notify.success(data?.message);
                validateUser();
                Report.success(
                    'Felicidades',
                    `!Felicidades! conseguiste (${roulettePrize}), disfruta tus jugadas en nuestro bingo Fenabocol.`,
                    'Okay',
                    handleCloseMod
                );
            }
        } catch (error) {
            validErrors(error);
        } finally {
            Loading.remove();
            loadData();
        }
    };

    return (
        <Card
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                maxWidth: 480,
                maxHeight: '92vh',
                overflow: 'auto',
                borderRadius: '22px',
                outline: 'none',
            }}
        >
            <CardContent>
                <Header>
                    <Box className="title-wrap">
                        <Box className="icon-box">
                            <CardGiftcardOutlinedIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6">Tus regalos</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Reclama jugadas o gira la ruleta de premios.
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Header>

                <Divider sx={{ my: 2 }} />

                {loading ? (
                    <LoaderBox>
                        <CircularProgress size={28} />
                        <Typography variant="caption" color="text.secondary">
                            Buscando regalos disponibles…
                        </Typography>
                    </LoaderBox>
                ) : giftsList.length === 0 ? (
                    <EmptyBox>
                        <RedeemOutlinedIcon />
                        <Typography sx={{ fontWeight: 700, color: '#10285f' }}>
                            No tienes regalos disponibles
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Cuando tengas un regalo activo aparecerá aquí para
                            que puedas reclamarlo.
                        </Typography>
                    </EmptyBox>
                ) : (
                    <Stack spacing={1.2}>
                        {giftsList.map((item) => {
                            const isRoulette = !!item?.gift_type;
                            const moves = item?.moves ?? 0;
                            const isClaiming = claimingId === item?.id;
                            return (
                                <GiftItem key={item?.id} $roulette={isRoulette}>
                                    <Box className="gift-icon">
                                        {isRoulette ? (
                                            <CasinoOutlinedIcon />
                                        ) : (
                                            <CardGiftcardOutlinedIcon />
                                        )}
                                    </Box>
                                    <Box className="gift-body">
                                        <Typography className="gift-title">
                                            {isRoulette ? 'Ruleta de premios' : `${moves} jugada${moves > 1 ? 's' : ''}`}
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.4 }}>
                                            <Chip
                                                size="small"
                                                label={isRoulette ? 'Sorpresa' : 'Premio fijo'}
                                                sx={{
                                                    background: isRoulette ? 'rgba(214, 170, 58, 0.18)' : 'rgba(46, 125, 50, 0.14)',
                                                    color: isRoulette ? '#8a5a00' : '#1e6a36',
                                                    fontWeight: 700,
                                                    border: 'none',
                                                }}
                                            />
                                            {item?.expires_at && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`Vence ${new Date(item.expires_at).toLocaleDateString('es-CO')}`}
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                    <LoadingButton
                                        size="small"
                                        variant="contained"
                                        loading={isClaiming && !isRoulette}
                                        startIcon={isRoulette ? <CasinoOutlinedIcon /> : <RedeemOutlinedIcon />}
                                        onClick={() => handleOpeneMod(item)}
                                        sx={{
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 800,
                                            background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                            },
                                        }}
                                    >
                                        <span>{isRoulette ? 'Girar' : 'Reclamar'}</span>
                                    </LoadingButton>
                                </GiftItem>
                            );
                        })}
                    </Stack>
                )}
            </CardContent>

            <Modal open={open} onClose={handleCloseMod}>
                {open ? (
                    <>
                        <Typography
                            variant="h2"
                            color="#f4d03f"
                            textAlign="center"
                            sx={{
                                position: 'absolute',
                                top: 'calc(50% - 280px)',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                            }}
                            fontFamily="Impact"
                        >
                            Ruleta Fenabocol
                        </Typography>
                        <Roulette
                            setRoulettePrize={setRoulettePrize}
                            setRouletteValue={setRouletteValue}
                            wasActive={wasActive}
                            setWasActive={setWasActive}
                        />
                        {rouletteValue && roulettePrize && wasActive && (
                            <ButtonClaim onClick={registerUserGift}>Reclamar</ButtonClaim>
                        )}
                    </>
                ) : (
                    <>Cargando Regalo...</>
                )}
            </Modal>
        </Card>
    );
};

export default Gifts;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.5rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
`;

const LoaderBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    padding: 2rem 1rem;
`;

const EmptyBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    padding: 2rem 1rem;
    text-align: center;
    border-radius: 16px;
    background: rgba(16, 40, 95, 0.025);
    border: 1px dashed rgba(15, 34, 77, 0.16);

    svg {
        font-size: 2.2rem;
        color: rgba(15, 34, 77, 0.35);
        margin-bottom: 0.2rem;
    }
`;

const GiftItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.8rem 0.95rem;
    border-radius: 14px;
    background: ${({ $roulette }) =>
        $roulette ? 'rgba(214, 170, 58, 0.06)' : 'rgba(16, 40, 95, 0.03)'};
    border: 1px solid ${({ $roulette }) =>
        $roulette ? 'rgba(214, 170, 58, 0.28)' : 'rgba(15, 34, 77, 0.1)'};
    transition: 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 14px rgba(15, 34, 77, 0.06);
    }

    .gift-icon {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        background: ${({ $roulette }) =>
            $roulette ? 'rgba(214, 170, 58, 0.22)' : 'rgba(16, 40, 95, 0.08)'};
        color: ${({ $roulette }) => ($roulette ? '#8a5a00' : '#10285f')};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
            font-size: 1.3rem;
        }
    }

    .gift-body {
        flex: 1;
        min-width: 0;
    }

    .gift-title {
        font-weight: 900;
        color: #10285f;
        font-size: 1rem;
        line-height: 1.2;
    }
`;

const ButtonClaim = styled.button`
    align-items: center;
    background-color: initial;
    background-image: radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%),
                radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%);
    border-radius: 42px;
    border-width: 0;
    box-shadow: rgba(254, 219, 55, 0.24) 0 2px 2px, rgba(209, 180, 100, 0.4) 0 8px 12px;
    color: #FFFFFF;
    cursor: pointer;
    display: flex;
    font-family: Quicksand, sans-serif;
    font-size: 18px;
    font-weight: 700;
    justify-content: center;
    letter-spacing: 0.04em;
    line-height: 16px;
    margin: 0;
    padding: 18px 18px;
    text-align: center;
    text-decoration: none;
    text-shadow: rgba(255, 255, 255, 0.4) 0 0 4px, rgba(255, 255, 255, 0.2) 0 0 12px, rgba(57, 31, 91, 0.6) 1px 1px 4px, rgba(57, 31, 91, 0.32) 4px 4px 16px;
    user-select: none;
    -webkit-user-select: none;
    touch-action: manipulation;
    vertical-align: baseline;
    position: absolute;
    top: calc(50% + 300px);
    left: 50%;
    transform: translate(-50%, -50%);
    transition: all 0.5s ease;

    &:hover {
        font-size: 22px;
        text-decoration: underline;
    }

    @media (min-width: 768px) {
        font-size: 21px;
        padding: 18px 34px;
    }
    @media (max-width: 576px) {
        top: calc(50% + 250px);
    }
`;
